/**
 * PortsContext — composition-root aggregate React Context (T015).
 *
 * Canonical shape reference: `specs/011-pwa-prototype-convergence/contracts/ports-provider.md`.
 * Architecture rationale: `specs/011-pwa-prototype-convergence/research.md` §3 (port architecture)
 * + §4 (port-isolation mechanism).
 *
 * Single place where concrete port adapters are bound. `main.tsx` composes
 * the demo defaults and passes them to `<PortsProvider ports={...}>`; tests
 * use the same `PortsProvider` via `renderAppWithPorts(overrides?)` (T016)
 * with shallow-merged overrides. No per-port React Context; no bundler hack;
 * no `vi.mock` —.
 *
 * Cluster 1 implements `session` + `language` only. Later slots such as
 * `content` are declared here as optional types so each subsequent cluster
 * fills them in without churning this file; when a slot is absent, the
 * matching per-port hook throws a clear missing-port error.
 * `content` is owned by its domain module
 * (`../content/ContentReaderPort.ts` for the interface,
 * `../content/hooks/useContent.ts` for the consumer hook), promoted in
 * the content-and-knowledge feature PR B / TA14.
 *
 * `null` sentinel (vs `undefined`) differentiates "no provider in tree" from
 * "provider mounted but adapter absent for this slot". Per-port hooks
 * interpret the sentinel as "missing provider" and throw accordingly.
 */

import { createContext } from 'react';

import type { ContactVerificationPort } from '../contact-verification/ContactVerificationPort.js';
import type { PostalCodeLookupPort } from '../contact-verification/PostalCodeLookupPort.js';
import type { ContentReaderPort } from '../content/ContentReaderPort.js';
import type { FeedbackSubmitPort } from '../feedback/FeedbackSubmitPort.js';
import type { LanguagePort } from '../i18n/LanguagePort.js';
import type { CasesReaderPort } from '../member-cases/CasesReaderPort.js';
import type { MessageComposePort } from '../member-messaging/compose/MessageComposePort.js';
import type { MemberProfilePort } from '../member-profile/MemberProfilePort.js';
import type { MembershipBillingPort } from '../membership-billing/MembershipBillingPort.js';
import type { MembershipCertificatePort } from '../membership-certificate/MembershipCertificatePort.js';
import type { MembershipResignationPort } from '../membership-resignation/MembershipResignationPort.js';
import type { AccountDeletionPort } from '../my-data/AccountDeletionPort.js';
import type { MyDataExportPort } from '../my-data/MyDataExportPort.js';
import type { MyDataPort } from '../my-data/MyDataPort.js';
import type { SessionPort } from '../session/SessionPort.js';

export interface Ports {
  readonly session: SessionPort;
  readonly language: LanguagePort;
  // Cluster 2:
  readonly content?: ContentReaderPort;
  // Cluster 4:
  readonly memberProfile?: MemberProfilePort | undefined;
  // Cluster 5 (US3):
  /**
   * Membership fee + certificate ports (026-proto-convergence-v2, US3). The
   * certificate port is the REAL Oma+-generated adapter over
   * `member.certificate` (049/D-215 — no integration-service certificate
   * path); the billing port reads invoices via `member.billing` (041). Optional
   * in the type so test compositions that don't exercise the Omat tiedot fee /
   * certificate surfaces can omit them; the consumer hooks throw a clear
   * missing-port error when a surface needs an absent slot. The explicit
   * `| undefined` (matching `memberProfile`) lets a test pass the slot as
   * `undefined` under `exactOptionalPropertyTypes` to exercise the
   * missing-port path.
   */
  readonly membershipBilling?: MembershipBillingPort | undefined;
  readonly membershipCertificate?: MembershipCertificatePort | undefined;
  /**
   * Member resignation (eroilmoitus) write (056) over `member.resignation`. A
   * dedicated port, not part of `messageCompose`: resignation is not a message
   * (D-235). Optional so test compositions that never open the resignation step
   * can omit it; `useMembershipResignation` throws a clear missing-port error.
   */
  readonly membershipResignation?: MembershipResignationPort | undefined;
  // 033 US1 (Tallennetut tiedot):
  /**
   * Consolidated GDPR-access read surface (033 US1, T024) — the "Tallennetut
   * tiedot" view in the Omat tiedot hub. Production wires the REAL tRPC-backed
   * `TrpcMyDataReader` over `member.myData.view` (the first real consumer of
   * the 033 Foundational layer — no demo adapter). Optional in the type so
   * test compositions that don't exercise the surface omit it; the `useMyData`
   * consumer hook throws a clear missing-port error when the surface needs the
   * absent slot. The explicit `| undefined` (matching `membershipBilling`)
   * lets a test pass the slot as `undefined` under
   * `exactOptionalPropertyTypes` to exercise the missing-port path.
   */
  readonly myData?: MyDataPort | undefined;
  // 034 US1 (Omat asiat):
  /**
   * Member cases-list read surface (034 US1) — the REAL tRPC-backed
   * `TrpcCasesReader` over `member.cases.list` (route-on-read, first page; no
   * local copy — FR-009). Optional in the type so test compositions that don't
   * exercise the surface omit it; `useCases` throws a clear missing-port error
   * when the surface needs the absent slot. The `| undefined` (matching `myData`)
   * lets a test pass the slot as `undefined` under `exactOptionalPropertyTypes`.
   */
  readonly memberCases?: CasesReaderPort | undefined;
  // Cluster 6 (US4):
  /**
   * Contact-verification cadence port (026-proto-convergence-v2, US4). Demo
   * adapter now; the real adapter arrives with 020-integration-service and
   * refuses to persist until #125 (member-write-back). Optional in the type so
   * test compositions that don't exercise the "Tarkista yhteystiedot" prompt can
   * omit it; the Stage-B consumer hook throws a clear missing-port error when a
   * surface needs the absent slot. The explicit `| undefined` (matching
   * `membershipBilling`) lets a test pass the slot as `undefined` under
   * `exactOptionalPropertyTypes` to exercise the missing-port path.
   */
  readonly contactVerification?: ContactVerificationPort | undefined;
  /**
   * Postal-code → city lookup port (026-proto-convergence-v2, US4). Demo adapter
   * (a static map, no network) now; the real adapter arrives with
   * 020-integration-service. Optional in the type AND optional to the form: the
   * contact-update form works without it, so the `usePostalCodeLookup` consumer
   * hook TOLERATES an absent slot (it yields `{ city: null }` rather than
   * throwing). The explicit `| undefined` (matching `contactVerification`) lets a
   * test pass the slot as `undefined` under `exactOptionalPropertyTypes` to
   * exercise the no-autofill path.
   */
  readonly postalCodeLookup?: PostalCodeLookupPort | undefined;
  /**
   * Client-side message-compose port (027-member-message-compose, US1; 050 US1).
   * The composition wires the REAL `TrpcMessageComposeAdapter` (forwards to the
   * `memberMessaging.*` tRPC procedures — the send creates a real case). The
   * `DemoMessageComposeAdapter` is now a TEST fixture only (ships the US1 topic
   * tree + a configurable submit result for the wizard tests) — it is NOT
   * composed at runtime. Optional in the type so test compositions that don't
   * exercise the compose surface can omit it; the `useComposeSubmit` hook throws
   * a clear missing-port error when the surface needs the absent slot.
   */
  readonly messageCompose?: MessageComposePort | undefined;
  /**
   * Member account-erasure port (033 US2, T029). Backs the
   * `DeleteAccountConfirm` irreversibility flow via `useDeleteAccount`;
   * the real tRPC adapter (→ `member.myData.requestDeletion`) arrives
   * with the my-data end-to-end wiring. Optional in the type so
   * compositions that don't exercise the deletion flow can omit it; the
   * consumer hook throws a clear missing-port error when the surface
   * needs the absent slot. The explicit `| undefined` (matching
   * `memberProfile`) lets a test pass the slot as `undefined` under
   * `exactOptionalPropertyTypes` to exercise the missing-port path.
   */
  readonly accountDeletion?: AccountDeletionPort | undefined;
  /**
   * Member data-portability port (033 US5, T041). Backs the export affordance
   * in Tallennetut tiedot via `useExport`; production wires the REAL
   * tRPC-backed `TrpcMyDataExportAdapter` over `member.myData.export`.
   * Optional in the type so compositions that don't exercise the export flow
   * can omit it; the consumer hook throws a clear missing-port error when the
   * surface needs the absent slot. The explicit `| undefined` (matching
   * `accountDeletion`) lets a test pass the slot as `undefined` under
   * `exactOptionalPropertyTypes` to exercise the missing-port path.
   */
  readonly myDataExport?: MyDataExportPort | undefined;
  /**
   * Member feedback SUBMIT port (033 US5, T040). Backs `FeedbackModal` via
   * `useSubmitFeedback`, retiring its session-local stub (FR-057); production
   * wires the REAL tRPC-backed `TrpcFeedbackSubmitAdapter` over
   * `member.feedback.submit`. Optional in the type AND optional to the surface:
   * the feedback tab is global member chrome, so `useSubmitFeedback` TOLERATES
   * an absent slot (it yields `null` and the modal hides its submit affordance
   * rather than faking a success) — the `postalCodeLookup` posture.
   */
  readonly feedbackSubmit?: FeedbackSubmitPort | undefined;
}

export const PortsContext = createContext<Ports | null>(null);
